/**
 * Drop-pipeline pure reducer — Work Package B.
 *
 * Spec: LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §9 + §10
 * Phase: INIT-2026-Q3-SCENE-HIERARCHY Work Package B (drop-crs-integrator)
 *
 * All state transitions are pure: no fetch calls, no side effects, no timers.
 * Returns `Object.freeze`d state to enforce immutability at runtime.
 *
 * State-machine transitions (documented in dropPipelineTypes.ts):
 *
 *   idle + preflight →
 *     preflight-ready          if triage.directive === "identity"
 *     awaiting-crs-decision    otherwise
 *
 *   awaiting-crs-decision + crs-decision →
 *     cancelled                if decision.optionKey === "reject"
 *     ready-to-pin             otherwise (alignedToEdgeId minted)
 *
 *   preflight-ready + pin → pinning
 *   ready-to-pin   + pin → pinning
 *   pinning        + pin → no-op (idempotent guard)
 *
 *   pinning + pin-success → pinned (lastPinnedAt set)
 *
 *   any + cancel → cancelled
 *   any + error  → error (errorMessage set)
 *
 *   cancelled | pinned | error + preflight → resets to preflight-ready
 *                                            or awaiting-crs-decision
 */

import type {
  DropPipelineEvent,
  DropPipelineState,
} from './dropPipelineTypes';

// ─── Initial state ────────────────────────────────────────────────────────────

/**
 * Default state exported for `useReducer` initialisation in `SceneTreeDropArea`.
 */
export const DROP_PIPELINE_INITIAL: DropPipelineState = Object.freeze({
  stage: 'idle',
  inspection: null,
  crsTriage: null,
  crsDecision: null,
  errorMessage: null,
  alignedToEdgeId: null,
  lastPinnedAt: null,
} as const);

// ─── Reducer ─────────────────────────────────────────────────────────────────

/**
 * Pure reducer for the drop-ingest pipeline state machine.
 *
 * @param state  — current (frozen) pipeline state
 * @param event  — discriminated event to process
 * @returns new frozen state; returns the same reference when no transition applies
 */
export function dropPipelineReducer(
  state: DropPipelineState,
  event: DropPipelineEvent,
): DropPipelineState {
  switch (event.kind) {
    // ── preflight ────────────────────────────────────────────────────────────
    case 'preflight': {
      // Allow re-entry from terminal states to support "drop another file"
      const isTerminal =
        state.stage === 'cancelled' ||
        state.stage === 'pinned' ||
        state.stage === 'error';
      // Also allow reset from any active state (e.g. new drop while card open)
      const allowReset = isTerminal || state.stage !== 'pinning';

      if (!allowReset) {
        // Pinning in progress — ignore concurrent drops
        return state;
      }

      const needsCrsDecision = event.triage.directive !== 'identity';

      return Object.freeze({
        stage: needsCrsDecision ? 'awaiting-crs-decision' : 'preflight-ready',
        inspection: event.inspection,
        crsTriage: event.triage,
        crsDecision: null,
        errorMessage: null,
        alignedToEdgeId: null,
        lastPinnedAt: state.lastPinnedAt,
      });
    }

    // ── crs-decision ─────────────────────────────────────────────────────────
    case 'crs-decision': {
      if (state.stage !== 'awaiting-crs-decision') {
        return state;
      }

      if (event.decision.optionKey === 'reject') {
        return Object.freeze({
          ...state,
          stage: 'cancelled',
          crsDecision: event.decision,
        });
      }

      // Mint a ULID-compatible edge ID using crypto.randomUUID()
      const alignedToEdgeId = crypto.randomUUID();

      return Object.freeze({
        ...state,
        stage: 'ready-to-pin',
        crsDecision: event.decision,
        alignedToEdgeId,
      });
    }

    // ── pin ───────────────────────────────────────────────────────────────────
    case 'pin': {
      if (state.stage === 'pinning') {
        // Idempotent guard — already in-flight
        return state;
      }

      if (state.stage !== 'preflight-ready' && state.stage !== 'ready-to-pin') {
        return state;
      }

      return Object.freeze({
        ...state,
        stage: 'pinning',
      });
    }

    // ── pin-success ──────────────────────────────────────────────────────────
    case 'pin-success': {
      if (state.stage !== 'pinning') {
        return state;
      }

      return Object.freeze({
        ...state,
        stage: 'pinned',
        lastPinnedAt: new Date().toISOString(),
      });
    }

    // ── cancel ────────────────────────────────────────────────────────────────
    case 'cancel': {
      return Object.freeze({
        ...state,
        stage: 'cancelled',
      });
    }

    // ── error ─────────────────────────────────────────────────────────────────
    case 'error': {
      return Object.freeze({
        ...state,
        stage: 'error',
        errorMessage: event.message,
      });
    }

    default: {
      // Exhaustiveness check — TypeScript will error if a case is missed
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}
